// Enhanced PartnersScreen.js - Integrating existing SubcontractorsVendorList functionality
// This combines our modern B2B portal design with the rich vendor management features

import React, { useState, useEffect } from 'react';
import {



  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  FlatList,
  TextInput,
  Image,
  Alert,
  RefreshControl,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
} from '@expo/vector-icons';
import { COLORS, SIZES, FONTS } from '../../../constants/theme';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width } = Dimensions.get('window');

// Enhanced mock data for B2B partners (clients, subcontractors, suppliers)
const mockPartners = {
  clients: [
    {
      id: 'client-001',
      name: 'Global Tech Solutions Ltd',
      type: 'Client',
      specialization: ['Software Development', 'IT Consulting'],
      ratings: { quality: 4.8, reliability: 4.7, payment: 4.9, communication: 4.6 },
      overall: 4.8,
      contact: {
        phone: '+44 20 1234 5678',
        email: 'contact@globaltech.co.uk',
        primaryContact: 'Sarah Johnson, CTO',
      },
      location: 'London, UK',
      status: 'Active',
      contractValue: '£250,000',
      isFavorite: true,
      lastInteraction: '2025-09-10',
      projectsActive: 3,
      relationshipDuration: '2 years',
      contractInfo: {
        startDate: '01/01/2023',
        endDate: '31/12/2025',
        paymentTerms: 'Net 30',
      },
    },
    {
      id: 'client-002',
      name: 'Innovate Manufacturing Corp',
      type: 'Client',
      specialization: ['Manufacturing', 'Supply Chain'],
      ratings: { quality: 4.5, reliability: 4.3, payment: 4.7, communication: 4.4 },
      overall: 4.5,
      contact: {
        phone: '+44 161 2345 6789',
        email: 'procurement@innovatemfg.co.uk',
        primaryContact: 'Michael Brown, Procurement Director',
      },
      location: 'Manchester, UK',
      status: 'Active',
      contractValue: '£180,000',
      isFavorite: false,
      lastInteraction: '2025-09-08',
      projectsActive: 2,
      relationshipDuration: '1.5 years',
      contractInfo: {
        startDate: '15/03/2023',
        endDate: '14/03/2026',
        paymentTerms: 'Net 45',
      },
    },
  ],
  subcontractors: [
    {
      id: 'sub-001',
      name: 'Elite Construction Services',
      type: 'Subcontractor',
      specialization: ['Concrete Work', 'Foundation', 'Structural'],
      ratings: { quality: 4.7, reliability: 4.5, cost: 3.9, timeliness: 4.2 },
      overall: 4.3,
      contact: {
        phone: '+44 20 1234 5678',
        email: 'info@eliteconstruction.co.uk',
        emergencyPhone: '+44 20 1234 5679',
        primaryContact: 'James Wilson, Project Manager',
      },
      location: 'London, UK',
      status: 'Active',
      teamSize: '15-20 workers',
      isFavorite: true,
      lastUsed: '2025-09-12',
      projectsCompleted: 8,
      onTimeRate: 95,
      contractInfo: {
        startDate: '01/01/2023',
        endDate: '31/12/2024',
        hourlyRate: '£45/hour',
      },
    },
    {
      id: 'sub-002',
      name: 'Premier Electrical Contractors',
      type: 'Subcontractor',
      specialization: ['Electrical', 'Lighting', 'Power Systems'],
      ratings: { quality: 4.8, reliability: 4.3, cost: 3.7, timeliness: 4.0 },
      overall: 4.2,
      contact: {
        phone: '+44 20 2345 6789',
        email: 'info@premierelectrical.co.uk',
        emergencyPhone: '+44 20 2345 6790',
        primaryContact: 'Emma Davis, Operations Manager',
      },
      location: 'Manchester, UK',
      status: 'Active',
      teamSize: '8-12 workers',
      isFavorite: false,
      lastUsed: '2025-09-10',
      projectsCompleted: 7,
      onTimeRate: 89,
      contractInfo: {
        startDate: '15/02/2023',
        endDate: '14/02/2025',
        hourlyRate: '£42/hour',
      },
    },
  ],
  suppliers: [
    {
      id: 'sup-001',
      name: 'Advanced Materials Supply Co',
      type: 'Supplier',
      specialization: ['Steel', 'Concrete', 'Building Materials'],
      ratings: { quality: 4.6, reliability: 4.4, pricing: 4.1, delivery: 4.3 },
      overall: 4.4,
      contact: {
        phone: '+44 121 3456 7890',
        email: 'orders@advancedmaterials.co.uk',
        primaryContact: 'Robert Taylor, Sales Manager',
      },
      location: 'Birmingham, UK',
      status: 'Active',
      deliveryRadius: '200 miles',
      isFavorite: true,
      lastOrder: '2025-09-11',
      ordersCompleted: 15,
      onTimeDelivery: 93,
      contractInfo: {
        startDate: '01/04/2023',
        endDate: '31/03/2025',
        paymentTerms: 'Net 30',
      },
    },
  ],
};

const PartnersScreen = ({ navigation }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [partners, setPartners] = useState([]);

  const filters = ['All', 'Clients', 'Subcontractors', 'Suppliers'];

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    // Try to load from API first
    let allPartners = [];
    try {
      const response = await apiRequest('partners');
      if (response.success && response.data) {
        allPartners = response.data;
      }
    } catch (apiErr) {
      console.warn('API unavailable, using mock data:', apiErr.message);
    }

    // Fall back to mock data if API returned nothing
    if (allPartners.length === 0) {
      allPartners = [
        ...mockPartners.clients,
        ...mockPartners.subcontractors,
        ...mockPartners.suppliers,
      ];
    }
    setPartners(allPartners);
  };

  const filteredPartners = partners.filter(partner => {
    const matchesFilter = activeFilter === 'All' || 
      (activeFilter === 'Clients' && partner.type === 'Client') ||
      (activeFilter === 'Subcontractors' && partner.type === 'Subcontractor') ||
      (activeFilter === 'Suppliers' && partner.type === 'Supplier');
    
    const matchesSearch = String(partner.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      partner.specialization.some(spec => spec.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  const onRefresh = () => {
    setRefreshing(true);
    loadPartners();
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Client': return COLORS.primary;
      case 'Subcontractor': return COLORS.success;
      case 'Supplier': return COLORS.warning;
      default: return COLORS.gray;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Client': return 'account-tie';
      case 'Subcontractor': return 'hammer-wrench';
      case 'Supplier': return 'truck-delivery';
      default: return 'account';
    }
  };

  const renderStarRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Ionicons key={i} name="star" size={14} color="#FFD700" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Ionicons key="half" name="star-half" size={14} color="#FFD700" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Ionicons key={`empty-${i}`} name="star-outline" size={14} color="#FFD700" />
      );
    }

    return <View style={styles.ratingContainer}>{stars}</View>;
  };

  const renderPartnerCard = ({ item }) => (
    <TouchableOpacity
      style={styles.partnerCard}
      onPress={() => navigation.navigate('PartnerDetails', { partner: item })}
    >
      <View style={styles.cardHeader}>
        <View style={styles.partnerInfo}>
          <View style={styles.nameRow}>
            <MaterialCommunityIcons 
              name={getTypeIcon(item.type)} 
              size={20} 
              color={getTypeColor(item.type)} 
            />
            <Text style={styles.partnerName}>{item.name}</Text>
            {item.isFavorite && (
              <Ionicons name="heart" size={16} color="#EF4444" />
            )}
          </View>
          <View style={[styles.typeBadge, { backgroundColor: getTypeColor(item.type) }]}>
            <Text style={styles.typeText}>{item.type}</Text>
          </View>
        </View>
        <TouchableOpacity style={styles.moreButton}>
          <MaterialCommunityIcons name="dots-vertical" size={20} color={COLORS.gray} />
        </TouchableOpacity>
      </View>

      <View style={styles.cardContent}>
        <View style={styles.ratingRow}>
          {renderStarRating(item.overall)}
          <Text style={styles.ratingText}>{item.overall.toFixed(1)}</Text>
        </View>

        <Text style={styles.location}>
          <Ionicons name="location-outline" size={14} color={COLORS.gray} />
          {' '}{item.location}
        </Text>

        <View style={styles.specializationContainer}>
          {item.specialization.slice(0, 2).map((spec, index) => (
            <View key={index} style={styles.specializationTag}>
              <Text style={styles.specializationText}>{spec}</Text>
            </View>
          ))}
          {item.specialization.length > 2 && (
            <Text style={styles.moreSpecializations}>
              +{item.specialization.length - 2} more
            </Text>
          )}
        </View>

        <View style={styles.cardFooter}>
          <View style={styles.statusContainer}>
            <View style={[styles.statusDot, { backgroundColor: item.status === 'Active' ? COLORS.success : COLORS.gray }]} />
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
          <Text style={styles.lastInteraction}>
            Last: {item.lastInteraction || item.lastUsed || item.lastOrder}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Partners</Text>
        <TouchableOpacity style={styles.addButton}>
          <Ionicons name="add" size={24} color={COLORS.white} />
        </TouchableOpacity>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Ionicons name="search-outline" size={20} color={COLORS.gray} style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search partners..."
          placeholderTextColor={COLORS.gray}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>

      {/* Filter Buttons */}
      <View style={styles.filterContainer}>
        {filters.map(filter => (
          <TouchableOpacity
            key={filter}
            style={[styles.filterButton, activeFilter === filter && styles.activeFilter]}
            onPress={() => setActiveFilter(filter)}
          >
            <Text style={[styles.filterText, activeFilter === filter && styles.activeFilterText]}>
              {filter}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Partners List */}
      <FlatList
        data={filteredPartners}
        renderItem={renderPartnerCard}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="account-group-outline" size={64} color={COLORS.gray} />
            <Text style={styles.emptyText}>No partners found</Text>
            <Text style={styles.emptySubtext}>Try adjusting your search or filters</Text>
          </View>
        }
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.lightGray,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
    paddingTop: SIZES.padding,
    paddingBottom: SIZES.base,
  },
  headerTitle: {
    ...FONTS.h1,
    color: COLORS.darkGray,
  },
  addButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: SIZES.radius,
    marginHorizontal: SIZES.padding,
    paddingHorizontal: SIZES.base * 1.5,
    marginVertical: SIZES.base,
  },
  searchIcon: {
    marginRight: SIZES.base,
  },
  searchInput: {
    flex: 1,
    height: 45,
    ...FONTS.body3,
    color: COLORS.darkGray,
  },
  filterContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: SIZES.padding,
    paddingVertical: SIZES.base * 1.5,
  },
  filterButton: {
    paddingVertical: SIZES.base,
    paddingHorizontal: SIZES.base * 1.5,
    borderRadius: SIZES.radius * 2,
    backgroundColor: COLORS.white,
  },
  activeFilter: {
    backgroundColor: COLORS.primary,
  },
  filterText: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontWeight: '500',
  },
  activeFilterText: {
    color: COLORS.white,
  },
  listContainer: {
    paddingHorizontal: SIZES.padding,
    paddingBottom: 80,
  },
  partnerCard: {
    backgroundColor: COLORS.white,
    borderRadius: SIZES.radius,
    padding: SIZES.base * 2,
    marginBottom: SIZES.base * 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 3,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: SIZES.base,
  },
  partnerInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SIZES.base / 2,
  },
  partnerName: {
    ...FONTS.h3,
    fontWeight: '600',
    color: COLORS.darkGray,
    marginLeft: SIZES.base,
    flex: 1,
  },
  typeBadge: {
    paddingHorizontal: SIZES.base,
    paddingVertical: SIZES.base / 2,
    borderRadius: SIZES.radius,
    alignSelf: 'flex-start',
  },
  typeText: {
    ...FONTS.body4,
    color: COLORS.white,
    fontSize: 12,
    fontWeight: 'bold',
  },
  moreButton: {
    padding: SIZES.base / 2,
  },
  cardContent: {
    marginTop: SIZES.base,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: SIZES.base,
  },
  ratingContainer: {
    flexDirection: 'row',
    marginRight: SIZES.base,
  },
  ratingText: {
    ...FONTS.body4,
    color: COLORS.darkGray,
    fontWeight: '600',
  },
  location: {
    ...FONTS.body4,
    color: COLORS.gray,
    marginBottom: SIZES.base,
  },
  specializationContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: SIZES.base,
  },
  specializationTag: {
    backgroundColor: COLORS.primaryLight,
    paddingHorizontal: SIZES.base,
    paddingVertical: SIZES.base / 2,
    borderRadius: SIZES.radius / 2,
    marginRight: SIZES.base / 2,
    marginBottom: SIZES.base / 2,
  },
  specializationText: {
    ...FONTS.body4,
    color: COLORS.primary,
    fontSize: 12,
  },
  moreSpecializations: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontSize: 12,
    fontStyle: 'italic',
  },
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: SIZES.base,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: SIZES.base / 2,
  },
  statusText: {
    ...FONTS.body4,
    color: COLORS.darkGray,
    fontSize: 12,
  },
  lastInteraction: {
    ...FONTS.body4,
    color: COLORS.gray,
    fontSize: 12,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 100,
  },
  emptyText: {
    ...FONTS.h3,
    color: COLORS.gray,
    marginTop: SIZES.base,
  },
  emptySubtext: {
    ...FONTS.body4,
    color: COLORS.gray,
    marginTop: SIZES.base / 2,
  },
});

export default PartnersScreen;
